import {
  Form,
  GetApiErrorMessage,
  UserFormDataType,
  UserValidFormDataType,
  useMutationErrors,
  ActionButton,
  DetailLayout,
  getUserName,
  InfoIconUrl,
  ObjectHeader,
  Padding,
  useUserMutation,
  UserSchema,
  EditLock,
  ObjectType,
  Stack,
  FormField,
  FormInput,
  FormAlerts,
  FormSegments,
  FormSegmentLabel,
  FormDateRangePicker,
  useAlertContext,
  FormRef,
  FormCheckbox,
  useIsSuperadmin,
  useUserInfo,
  useNavigationBlockers,
} from '@atlas-frontend/sdk';
import { FC, RefObject, useCallback, useRef, useState } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useNavigateTo } from '../../paths/useNavigateTo';
import translations from './UserForm.translations.json';
import { UserBreadcrumbs } from './UserBreadcrumbs';
import styled from 'styled-components';
import { UserViewPermissionsFormTable } from './UserViewPermissionsFormTable';
import { UserRolesFormTable } from './UserRolesFormTable';
import { NewUserFormAutoComplete } from './NewUserFormAutoComplete';
import { path } from '../../paths';
import { Flex } from 'antd';

const { addTranslations, useTranslation, t } = i18nNsFactory('user-edit');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export type UserFormProps =
  | {
      mode: 'create';
      id?: never;
      defaultValues: UserFormDataType;
      onClickCancel: () => void;
    }
  | {
      mode: 'edit';
      id: number;
      defaultValues: UserFormDataType;
      onClickCancel: () => void;
    };

export const UserForm: FC<UserFormProps> = (props) => {
  const { mode, id, defaultValues, onClickCancel } = props;
  const { t } = useTranslation();
  const { mutateAsync } = useUserMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const navigateToUserEdit = useNavigateTo(path.userEdit);
  const { resetAlert, setSuccess } = useAlertContext();
  const ref: RefObject<FormRef<any> | null> = useRef(null);

  const { clear: clearBlockers } = useNavigationBlockers();
  const handleFinish = useCallback(
    (formData: UserValidFormDataType) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: id,
        data: formData,
      })
        .then((responseData) => {
          if (responseData) {
            clearBlockers();
            if (mode === 'create') {
              ref?.current?.supressBlock();
              navigateToUserEdit(
                { id: responseData.id },
                {
                  state: { noBlocker: true },
                },
              );
              setSuccess(t('alert.success.created'));
            } else {
              ref.current?.form.reset(formData);
              setSuccess();
            }
          }
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      id,
      mode,
      navigateToUserEdit,
      setSuccess,
      t,
      setMutationErrors,
      clearBlockers,
    ],
  );

  const isSuperadmin = useIsSuperadmin();
  const { user: sessionUser } = useUserInfo();
  const [hasValidPeriod, setHasValidPeriod] = useState(
    !defaultValues.isSuperadmin,
  );

  return (
    <Form
      schema={UserSchema.validFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
      formRef={ref}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.USER_DETAILS}
        breadCrumb={<UserBreadcrumbs data={{ user: defaultValues }} />}
        headerConfig={{
          actions: (
            <>
              <ActionButton type="abort" onClick={() => onClickCancel()} />
              <ActionButton type="formSubmit" />
            </>
          ),
        }}
        side={{
          header: (
            <Padding>
              <ObjectHeader
                upperText={defaultValues.username}
                lowerText={getUserName(defaultValues)}
                upperPlaceholder={
                  mode === 'create' ? t('username.placeholder') : undefined
                }
                lowerPlaceholder={
                  mode === 'create' ? t('name.placeholder') : undefined
                }
              />
            </Padding>
          ),
          content: (
            <Padding>
              <Stack size="md">
                {mode === 'create' && <UsernameFormField />}
                <FormField
                  name="nationalIdNumber"
                  label={t('nationalIdNumber.label')}
                >
                  <FormInput
                    name="nationalIdNumber"
                    maxLength={11}
                    type="text"
                    pattern="[0-9]*"
                  />
                </FormField>
                {hasValidPeriod && (
                  <FormField name="validPeriod" label={t('validPeriod.label')}>
                    <FormDateRangePicker
                      name="validPeriod"
                      allowEmpty={[true, true]}
                    />
                  </FormField>
                )}
                <Flex gap="small">
                  <FormField name="isAdmin">
                    <FormCheckbox name="isAdmin" disabled={!isSuperadmin}>
                      {t('admin.label')}
                    </FormCheckbox>
                  </FormField>
                  <FormField name="isSuperadmin">
                    <FormCheckbox
                      name="isSuperadmin"
                      disabled={!isSuperadmin || id === sessionUser.id}
                      onChange={(event) => {
                        if (event.target.checked) {
                          ref.current?.form.setValue('validPeriod', [
                            null,
                            null,
                          ]);
                          setHasValidPeriod(false);
                        } else {
                          setHasValidPeriod(true);
                        }
                      }}
                    >
                      {t('superadmin.label')}
                    </FormCheckbox>
                  </FormField>
                </Flex>
              </Stack>
            </Padding>
          ),
          footer:
            mode === 'edit' ? (
              <EditLock
                id={defaultValues.id}
                entity={defaultValues}
                objectType={ObjectType.User}
                onTimeout={onClickCancel}
              />
            ) : undefined,
        }}
        alert={<FormAlerts tLabel={t} />}
      >
        <Padding>
          <ContentWrapper>
            <UserFormSegments />
          </ContentWrapper>
        </Padding>
      </DetailLayout>
    </Form>
  );
};

const UserFormSegments = () => {
  return (
    <FormSegments
      segments={[
        {
          value: 'roles',
          label: (
            <FormSegmentLabel segmentValue="roles" formFieldName="roles">
              {t('segment-option.roles')}
            </FormSegmentLabel>
          ),
          children: <UserRolesFormTable key="roles" />,
        },
        {
          value: 'viewPermissions',
          label: (
            <FormSegmentLabel
              segmentValue="viewPermissions"
              formFieldName="viewPermissions"
            >
              {t('segment-option.view-permissions')}
            </FormSegmentLabel>
          ),
          children: <UserViewPermissionsFormTable key="viewPermissions" />,
        },
      ]}
    />
  );
};

const UsernameFormField = () => (
  <FormField name="username" label={t('username.label')} required>
    <NewUserFormAutoComplete
      name="username"
      placeholder={t('username.search.placeholder')}
    />
  </FormField>
);

const ContentWrapper = styled(Stack)`
  width: 100%;
  height: 100%;
  align-self: center;
  align-items: center;
`;
